import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { GraduationCap, Plus, X, RefreshCw, Download, Trash2, AlertCircle, Calendar, Webhook } from "lucide-react";
import { formatDate } from "@/lib/dates";

const WINDOW_PILL = {
  expired:      { cls: "pill-danger",  label: "Expired" },
  expiring_30:  { cls: "pill-danger",  label: "≤ 30 days" },
  expiring_60:  { cls: "pill-warning", label: "≤ 60 days" },
  expiring_90:  { cls: "pill-warning", label: "≤ 90 days" },
  ok:           { cls: "pill-success", label: "Current" },
};

export default function TrainingPage() {
  const [tab, setTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const params = tab === "all" ? {} : { window: tab };
      const qs = new URLSearchParams(params).toString();
      const [{ data: r }, { data: d }, { data: t }] = await Promise.all([
        api.get(`/training/records${qs ? `?${qs}` : ""}`),
        api.get("/training/dashboard"),
        api.get("/training/renewal-tasks?status=open"),
      ]);
      setRows(r || []);
      setDash(d);
      setTasks(t || []);
    } catch (e) { toast.error("Failed to load training records"); }
  };
  useEffect(() => { load(); }, [tab]);

  const runCheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training/run-expiry-check");
      toast.success(`Scanned ${data.records_scanned}; sent ${data.notifications_sent} alerts; created ${data.tasks_created} renewal tasks`);
      load();
    } catch (e) { toast.error("Run failed"); }
    finally { setBusy(false); }
  };

  const syncToCareConnect = async () => {
    if (!window.confirm("Push every active training record to Care Connect? Records that don't match Care Connect's matrix slugs (DBS / Safeguarding / Mental Capacity / Moving & Handling / Infection Control / First Aid / Fire Safety / etc.) will be skipped.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/training/sync-to-care-connect");
      toast.success(`Pushed ${data.pushed} record${data.pushed === 1 ? "" : "s"} to Care Connect${data.skipped_no_match ? ` · ${data.skipped_no_match} skipped (no matrix match)` : ""}${data.skipped_no_email ? ` · ${data.skipped_no_email} skipped (no work email)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Phase 3b</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><GraduationCap className="w-8 h-8" /> Training Records</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Track certificates, expiry dates and renewal tasks. Daily cron flags records expiring in 30/60/90 days.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary inline-flex items-center gap-1.5" onClick={syncToCareConnect} disabled={busy} data-testid="sync-care-connect-button">
            <Webhook className="w-4 h-4" /> Sync to Care Connect
          </button>
          <button className="btn-secondary inline-flex items-center gap-1.5" onClick={runCheck} disabled={busy} data-testid="run-expiry-check-button">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Run expiry check
          </button>
          <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => setShow(true)} data-testid="add-training-button">
            <Plus className="w-4 h-4" /> Add training record
          </button>
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(WINDOW_PILL).map(([w, sp]) => (
            <div key={w} className={`cc-card p-3 ${w === "expired" || w === "expiring_30" ? "border-l-4 border-[var(--cc-accent)]" : ""}`} data-testid={`training-summary-${w}`}>
              <div className="label-eyebrow mb-1 text-[10px]">{sp.label}</div>
              <div className="font-display text-2xl font-bold">{dash.by_window?.[w] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="cc-card p-4 mb-5 bg-[#fdf3ec] border-l-4 border-[var(--cc-accent)]" data-testid="renewal-tasks-banner">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-[var(--cc-accent)] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold mb-1">{tasks.length} renewal task{tasks.length === 1 ? "" : "s"} open</div>
              <div className="text-xs space-y-0.5">
                {tasks.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex justify-between" data-testid={`renewal-task-${t.id}`}>
                    <span>{t.employee_name} — <strong>{t.training_type}</strong> (due {formatDate(t.due_date)})</span>
                    <button className="btn-ghost text-[10px]" onClick={async () => { await api.post(`/training/renewal-tasks/${t.id}/dismiss`); toast.success("Dismissed"); load(); }} data-testid={`dismiss-task-${t.id}`}>Dismiss</button>
                  </div>
                ))}
                {tasks.length > 5 && <div className="text-[10px]">+{tasks.length - 5} more…</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 overflow-x-auto items-center">
        <div className="flex gap-1 overflow-x-auto">
          {["all", "expired", "expiring_30", "expiring_60", "expiring_90", "ok"].map((t) => (
            <button
              key={t}
              className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${tab === t ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
              onClick={() => setTab(t)}
              data-testid={`training-filter-${t}`}
            >{t.replace("_", " ")}</button>
          ))}
        </div>
        <div className="ml-auto">
          <input
            type="search"
            className="cc-input text-xs"
            placeholder="Filter by training type…"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="training-type-filter"
            style={{ minWidth: 220 }}
          />
        </div>
      </div>

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-2 label-eyebrow">Employee</th>
              <th className="px-4 py-2 label-eyebrow">Training</th>
              <th className="px-4 py-2 label-eyebrow">Completed</th>
              <th className="px-4 py-2 label-eyebrow">Expires</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Cert</th>
              <th className="px-4 py-2 label-eyebrow"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const q = typeFilter.trim().toLowerCase();
              const filtered = q ? rows.filter((r) => (r.training_type || "").toLowerCase().includes(q)) : rows;
              if (filtered.length === 0) {
                return (
                  <tr><td colSpan="7" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-training">
                    {q ? `No training records match "${typeFilter}".` : "No training records."}
                  </td></tr>
                );
              }
              return filtered.map((r) => {
              const sp = WINDOW_PILL[r.expiry_window || "ok"];
              return (
                <tr key={r.id} className="border-t border-[var(--cc-border)]" data-testid={`training-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium">{r.employee_name}</td>
                  <td className="px-4 py-2">{r.training_type}</td>
                  <td className="px-4 py-2 text-xs">{formatDate(r.completion_date)}</td>
                  <td className="px-4 py-2 text-xs">{r.expiry_date ? formatDate(r.expiry_date) : "—"}</td>
                  <td className="px-4 py-2"><span className={`pill ${sp.cls}`}>{sp.label}</span></td>
                  <td className="px-4 py-2">
                    {r.certificate ? (
                      <a className="btn-ghost text-xs inline-flex items-center gap-1" href={`${apiBase}/api/training/records/${r.id}/certificate`} target="_blank" rel="noreferrer" data-testid={`cert-link-${r.id}`}>
                        <Download className="w-3 h-3" /> {r.certificate.filename?.slice(0, 14) || "cert"}…
                      </a>
                    ) : (
                      <span className="text-xs italic text-[var(--cc-text-soft)]">none</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <button className="btn-ghost text-xs text-[var(--cc-accent)]" onClick={async () => {
                      if (!confirm("Archive this training record?")) return;
                      await api.delete(`/training/records/${r.id}`);
                      toast.success("Archived");
                      load();
                    }} data-testid={`archive-training-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {show && <AddTrainingModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function AddTrainingModal({ onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    employee_id: "", training_type: "", completion_date: new Date().toISOString().slice(0,10),
    expiry_date: "", provider: "", notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/employees").then((r) => setEmps(r.data || []));
    api.get("/training/suggested-types").then((r) => setTypes(r.data?.types || []));
  }, []);

  const submit = async () => {
    if (!form.employee_id || !form.training_type || !form.completion_date) {
      return toast.error("Employee, type and completion date required");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/training/records", fd);
      toast.success("Training record saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="add-training-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">Add training record</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Employee *</label>
          <select className="cc-select" value={form.employee_id} onChange={(e) => setForm({...form, employee_id: e.target.value})} data-testid="training-employee-select">
            <option value="">— Select —</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Training type *</label>
          <input className="cc-input" list="training-types-list" value={form.training_type} onChange={(e) => setForm({...form, training_type: e.target.value})} placeholder="e.g. Care Certificate" data-testid="training-type-input" />
          <datalist id="training-types-list">
            {types.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1">Completed *</label>
            <input type="date" className="cc-input" value={form.completion_date} onChange={(e) => setForm({...form, completion_date: e.target.value})} data-testid="training-completion-date" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Expires</label>
            <input type="date" className="cc-input" value={form.expiry_date} onChange={(e) => setForm({...form, expiry_date: e.target.value})} data-testid="training-expiry-date" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Provider</label>
          <input className="cc-input" value={form.provider} onChange={(e) => setForm({...form, provider: e.target.value})} data-testid="training-provider" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Certificate (PDF/DOC/image, ≤ 20 MB)</label>
          <input type="file" className="cc-input" onChange={(e) => setFile(e.target.files?.[0])} data-testid="training-cert-file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Notes</label>
          <textarea className="cc-input" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="training-submit">{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}
